import React, { useCallback, useMemo, useState } from 'react'
import { Box, Chip, Collapse, List, ListItem, Paper, Typography } from '@material-ui/core'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import DeleteOutlinedIcon from '@material-ui/icons/DeleteOutlined'
import _ from 'lodash'
import { RuleStepProps } from '../../CampaignModalSkeleton/CampaignModalSkeleton.types'
import IconButton from '../../../IconButton'
import Button from '../../../Button'
import {
	handlePurchasedProductToggle,
	handleOfferedProductToggle,
	handleAddOfferedProducts,
	handleDelete,
} from '../../MarketingCampaigns.promo.services'
import { BogoDiffRuleState } from './types'
import { ProductScopeSelector } from '../../CampaignModalSkeleton/components'

export function BogoDiffRuleStep({
	rule,
	setRule,
	description,
	sections,
	openErrorModal,
}: RuleStepProps<BogoDiffRuleState>) {
	const setOfferedProducts = useCallback(
		(updater: any) => {
			setRule((r) => ({
				...r,
				offeredProducts:
					typeof updater === 'function' ? updater(r.offeredProducts) : updater,
			}))
		},
		[setRule],
	)

	const setSelectedProducts = useCallback(
		(updater: any) => {
			setRule((r) => ({
				...r,
				selectedProducts:
					typeof updater === 'function' ? updater(r.selectedProducts) : updater,
			}))
		},
		[setRule],
	)

	const setFormValuesAdapter = useCallback(
		(updater: any) => {
			setRule((r) => {
				const patch = typeof updater === 'function' ? updater(r) : updater
				return { ...r, ...patch }
			})
		},
		[setRule],
	)

	const draft = rule.offeredProducts[0]

	const purchasedSelectedKeys = useMemo(
		() => (draft?.productKey ? [draft.productKey] : []),
		[draft?.productKey],
	)

	const offeredSelectedKeys = useMemo(
		() => (draft?.productOfferedKey ? [draft.productOfferedKey] : []),
		[draft?.productOfferedKey],
	)

	const purchasedDisabledKeys = useMemo(() => {
		if (!draft?.productKey) {
			return []
		}
		const out: string[] = []
		for (const s of sections) {
			for (const p of s.products) {
				if (p.key !== draft.productKey) {
					out.push(p.key)
				}
			}
		}
		return out
	}, [draft?.productKey, sections])

	const offeredDisabledKeys = useMemo(() => {
		const d = rule.offeredProducts[0]
		if (!d?.productKey) {
			return []
		}
		const out: string[] = []
		for (const s of sections) {
			for (const p of s.products) {
				if (p.key === d.productKey) {
					out.push(p.key)
					continue
				}
				if (d.productOfferedKey && p.key !== d.productOfferedKey) {
					out.push(p.key)
				}
			}
		}
		return out
	}, [rule.offeredProducts, sections])

	const [expandedOffered, setExpandedOffered] = useState(false)
	const [expandedPurchased, setExpandedPurchased] = useState(true)

	if (!_.size(sections)) {
		return (
			<Box>
				{description}
				<Typography variant="body2" color="textSecondary" className="py-4">
					Aucun produit en commun n&apos;est disponible. Merci de sélectionner à nouveau les
					points de vente ciblés.
				</Typography>
			</Box>
		)
	}

	return (
		<Box>
			{description}
			<Box mt={2} display="flex" flexDirection={{ xs: 'column', md: 'row' }} gridGap={16}>
				<Box flex={1} minWidth={0} style={{ maxHeight: '55vh', overflowY: 'auto' }}>
					<Paper
						variant="outlined"
						style={{ borderRadius: 10, overflow: 'hidden', marginBottom: 8 }}
					>
						<Box
							style={{
								padding: '11px 16px',
								background: '#fafafa',
								borderBottom: '1px solid rgba(0,0,0,0.08)',
							}}
						>
							<Typography variant="body2" style={{ fontWeight: 500, color: '#424242' }}>
								Liste des offres
							</Typography>
						</Box>
						<Box style={{ padding: '8px 12px' }}>
							{_.size(rule.selectedProducts) > 0 ? (
								<List disablePadding>
									{rule.selectedProducts.map((offer, index) => (
										<ListItem className="px-1" key={`${offer.productKey}-${offer.productOfferedKey}-${index}`}>
											<Box display="flex" alignItems="center" width="100%">
												<Box flex={1}>
													<Typography variant="body2">
														1 {offer.productName} <b>acheté</b> ⇒ 1 {offer.productOfferedName}{' '}
														<b>offert</b>
													</Typography>
												</Box>
												<IconButton
													onClick={() =>
														handleDelete(index, [setSelectedProducts], setFormValuesAdapter)
													}
													aria-label="supprimer"
												>
													<DeleteOutlinedIcon />
												</IconButton>
											</Box>
										</ListItem>
									))}
								</List>
							) : (
								<Box
									style={{
										textAlign: 'center',
										padding: 24,
										backgroundColor: '#fafafa',
										borderRadius: 10,
										width: '100%',
									}}
								>
									<Typography variant="body1" color="textSecondary">
										Aucune offre configurée
									</Typography>
									<Typography variant="caption" display="block" style={{ marginTop: 8 }}>
										Sélectionnez des produits puis cliquez sur « Ajouter une offre »
									</Typography>
								</Box>
							)}
						</Box>
					</Paper>
				</Box>

				<Box flex={1} minWidth={0}>
					<Paper
						variant="outlined"
						style={{ borderRadius: 10, overflow: 'hidden', marginBottom: 12 }}
					>
						<Box
							onClick={() => setExpandedPurchased((v) => !v)}
							style={{
								padding: '11px 16px',
								background: '#fafafa',
								borderBottom: expandedPurchased ? '1px solid rgba(0,0,0,0.08)' : 'none',
								cursor: 'pointer',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'space-between',
							}}
						>
							<Box display="flex" alignItems="center" style={{ gap: 8 }}>
								<Typography variant="body2" style={{ fontWeight: 500, color: '#424242' }}>
									Produit acheté
								</Typography>
								{!expandedPurchased && draft?.productKey && (
									<Chip
										label={draft.productName || '1 sélectionné'}
										size="small"
										variant="outlined"
										style={{
											borderColor: '#009973',
											color: '#009973',
											fontWeight: 500,
											fontSize: 11,
											height: 24,
										}}
									/>
								)}
							</Box>
							<ExpandMoreIcon
								style={{
									color: '#757575',
									transform: expandedPurchased ? 'rotate(0deg)' : 'rotate(-90deg)',
									transition: 'transform 0.2s',
									fontSize: 20,
								}}
							/>
						</Box>
						<Collapse in={expandedPurchased}>
							<ProductScopeSelector
								embedded
								sections={sections}
								selectedKeys={purchasedSelectedKeys}
								disabledKeys={purchasedDisabledKeys}
								onToggle={(product, sectionId) => {
									handlePurchasedProductToggle(product, sectionId, setOfferedProducts)
									setExpandedPurchased(false)
									setExpandedOffered(true)
								}}
								instructions="Choisissez le produit déclencheur de l&apos;offre"
							/>
						</Collapse>
					</Paper>

					<Paper variant="outlined" style={{ borderRadius: 10, overflow: 'hidden', marginBottom: 12 }}>
						<Box
							onClick={() => setExpandedOffered((v) => !v)}
							style={{
								padding: '11px 16px',
								background: '#fafafa',
								borderBottom: expandedOffered ? '1px solid rgba(0,0,0,0.08)' : 'none',
								cursor: 'pointer',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'space-between',
							}}
						>
							<Box display="flex" alignItems="center" style={{ gap: 4 }}>
								<Typography variant="body2" style={{ fontWeight: 500, color: '#424242' }}>
									Produit offert (cadeau)
								</Typography>
								{!expandedOffered && draft?.productOfferedKey && (
									<Chip
										label={draft.productOfferedName || '1 sélectionné'}
										size="small"
										variant="outlined"
										style={{
											borderColor: '#009973',
											color: '#009973',
											fontWeight: 500,
											fontSize: 11,
											height: 24,
										}}
									/>
								)}
							</Box>
							<ExpandMoreIcon
								style={{
									color: '#757575',
									transform: expandedOffered ? 'rotate(0deg)' : 'rotate(-90deg)',
									transition: 'transform 0.2s',
									fontSize: 20,
								}}
							/>
						</Box>
						<Collapse in={expandedOffered}>
							<ProductScopeSelector
								embedded
								sections={sections}
								selectedKeys={offeredSelectedKeys}
								disabledKeys={offeredDisabledKeys}
								onToggle={(product, sectionId) => {
									handleOfferedProductToggle(
										product,
										sectionId,
										setOfferedProducts,
										rule.offeredProducts,
										openErrorModal,
									)
								}}
								instructions="Choisissez le produit offert en cadeau"
								maxListHeight={165}
							/>
						</Collapse>
					</Paper>

					<Button
						onClick={() =>
							handleAddOfferedProducts(
								rule.offeredProducts,
								setSelectedProducts,
								setFormValuesAdapter,
								openErrorModal,
								setOfferedProducts,
								setExpandedOffered,
								setExpandedPurchased,
							)
						}
						disabled={
							!rule.offeredProducts.some((item) => item.productKey && item.productOfferedKey)
						}
						style={{ float: 'right', padding: '7px' }}
					>
						Ajouter une offre
					</Button>
				</Box>
			</Box>
		</Box>
	)
}